import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader, Check, X, Minus, ChevronUp, ChevronDown, Trash2, Plus, ListChecks } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../services/AuthContext';
import InspectorHome from './InspectorHome';

/**
 * The manager check — one item at a time, the way a good shop clipboard talks.
 *
 * Owner, Sep 18 2026 (his spec): show exactly one item, he answers done /
 * hold / skip / note, one short line back, next item. Morning before the
 * truck leaves, weekly on a shop day. Holds stay in the yard and the truck is
 * HOLD. Lists are his to edit. No lecture, no pep talk.
 */
type Mode = 'morning' | 'weekly';

/**
 * Owner, Sep 18 2026: "combine manager and inspector." The manager's page is
 * both of the manager's checks: the yard before the truck leaves, and the
 * week's lawns after the crew has been. Same page, two tabs; the yard check
 * is where it opens.
 */
export default function ManagerCheck() {
  const [params, setParams] = useSearchParams();
  // The manager hat lost lawn inspections (owner, Sep 28 2026: "remove lawn
  // to inspect") — the server refuses it too, so the tab would only 403.
  const { user } = useAuth();
  const canInspect = user?.role !== 'manager';
  const tab = canInspect && params.get('tab') === 'lawns' ? 'lawns' : 'yard';
  // Same query the lawns tab uses, so the count on the tab costs nothing extra.
  const { data: week } = useQuery(['inspection-week', undefined], () => api.get('/inspections/week').then(r => r.data), { retry: false, enabled: canInspect });
  const waiting = (week?.inspections || []).filter((i: any) => i.status === 'pending').length;
  const tabBtn = (on: boolean) => `flex-1 px-3 py-2 text-sm font-semibold ${on ? 'press bg-brand-600 text-black' : 'bg-white text-gray-700'}`;
  return (
    <div className="max-w-md mx-auto space-y-3">
      {canInspect && (
        <div className="flex rounded-xl border border-gray-300 overflow-hidden">
          <button onClick={() => setParams({})} className={tabBtn(tab === 'yard')}>Yard check</button>
          <button onClick={() => setParams({ tab: 'lawns' })} className={`${tabBtn(tab === 'lawns')} border-l border-gray-300`}>Lawns to inspect{waiting ? ` · ${waiting}` : ''}</button>
        </div>
      )}
      {tab === 'lawns' ? <div className="pb-10"><InspectorHome /></div> : <YardCheck />}
    </div>
  );
}

/**
 * The yard check on its own — the clipboard flow (one item, Done / Hold /
 * Skip, the list and adjust views, the hold list). Exported so the one
 * morning page (pages/Today) can carry it under its own header.
 */
export function YardCheck() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const mode = (params.get('mode') === 'weekly' ? 'weekly' : 'morning') as Mode;
  const setMode = (m: Mode) => setParams(m === 'morning' ? {} : { mode: m });
  const [note, setNote] = useState('');
  const [said, setSaid] = useState<string | null>(null);
  const [view, setView] = useState<'item' | 'list' | 'edit'>('item');

  const { data, isLoading } = useQuery(['manager-check', mode], () => api.get('/manager-check', { params: { mode } }).then(r => r.data), { staleTime: 0 });
  const check = data?.check;
  const refresh = () => qc.invalidateQueries(['manager-check']);
  const answer = useMutation(({ key, result }: { key: string; result: 'pass' | 'hold' | 'skip' }) =>
    api.post(`/manager-check/${check.id}/answer`, { key, result, note: note.trim() || undefined }).then(r => r.data), {
    onSuccess: (d: any) => { setSaid(d.said); setNote(''); qc.setQueryData(['manager-check', mode], (old: any) => ({ ...old, check: { ...d, said: undefined } })); qc.invalidateQueries('my-day'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that'); },
  });
  const passRest = useMutation(() => api.post(`/manager-check/${check.id}/pass-rest`).then(r => r.data), {
    onSuccess: (d: any) => { qc.setQueryData(['manager-check', mode], (old: any) => ({ ...old, check: d.check })); setSaid('Rest marked done.'); },
  });
  const restart = useMutation(() => api.post('/manager-check/start', { mode, restart: true }).then(r => r.data), {
    onSuccess: () => { setSaid(null); refresh(); },
  });

  if (isLoading || !check) return <div className="py-16 flex justify-center"><Loader className="h-6 w-6 animate-spin text-brand-600" /></div>;

  const modeLabel = mode === 'morning' ? 'Morning' : 'Weekly';
  const p = check.progress;
  const item = check.next;
  const fleet = data?.lists?.fleet;

  return (
    <div className="max-w-md mx-auto space-y-3 pb-10">
      {/* Mode + header line, like the clipboard */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
          <button onClick={() => setMode('morning')} className={`px-3 py-1.5 ${mode === 'morning' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}>Morning</button>
          <button onClick={() => setMode('weekly')} className={`px-3 py-1.5 border-l border-gray-300 ${mode === 'weekly' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}>Weekly shop</button>
        </div>
        <div className="ml-auto flex gap-1 text-xs">
          <button onClick={() => setView(view === 'list' ? 'item' : 'list')} className={`px-2.5 py-1.5 rounded-lg border ${view === 'list' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-700'}`}>list</button>
          <button onClick={() => setView(view === 'edit' ? 'item' : 'edit')} className={`px-2.5 py-1.5 rounded-lg border ${view === 'edit' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-300 text-gray-700'}`}>adjust</button>
        </div>
      </div>

      <div className="rounded-2xl bg-gray-900 text-white px-4 py-3 dark:bg-gray-100 dark:text-gray-900 dark:border dark:border-gray-200">
        <p className="text-xs uppercase tracking-widest text-gray-500">Keep Austin Mowed · {modeLabel}</p>
        <p className="text-2xl font-bold tabular-nums mt-0.5">{p.answered}/{p.total}</p>
        <p className="font-mono text-lg tracking-[0.15em] text-gray-300 mt-0.5 dark:text-gray-600">{p.dots}</p>
        {said && <p className="text-sm text-emerald-300 mt-2 dark:text-emerald-700">{said}</p>}
      </div>

      {view === 'edit' ? (
        <ListEditor lists={data.lists} mode={mode} onDone={() => { setView('item'); refresh(); }} />
      ) : view === 'list' ? (
        <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100">
          {check.items.map((i: any, idx: number) => (
            <div key={i.key} className="px-4 py-2.5 flex items-start gap-3 text-sm">
              <span className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center flex-shrink-0 text-white ${i.result === 'pass' ? 'bg-emerald-600' : i.result === 'hold' ? 'bg-red-600' : i.result === 'skip' ? 'bg-gray-400' : 'bg-gray-100'}`}>
                {i.result === 'pass' ? <Check className="h-3.5 w-3.5" /> : i.result === 'hold' ? <X className="h-3.5 w-3.5" /> : i.result === 'skip' ? <Minus className="h-3.5 w-3.5" /> : <span className="text-[10px] text-gray-500">{idx + 1}</span>}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block ${i.result ? 'text-gray-500' : 'text-gray-900 font-medium'}`}>{i.label}</span>
                {i.note && <span className="block text-xs text-gray-500">{i.note}</span>}
              </span>
              <span className="text-[11px] uppercase text-gray-500 w-10 text-right">{i.result === 'pass' ? 'done' : i.result || ''}</span>
            </div>
          ))}
        </div>
      ) : check.status === 'complete' && check.summary ? (
        <CompleteBlock s={check.summary} onWeekly={() => setMode('weekly')} onAdjust={() => setView('edit')} onRestart={() => restart.mutate()} />
      ) : item ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-gray-500">Item {p.answered + 1} of {p.total}</p>
          <p className="text-xl font-bold text-gray-900 leading-snug">{item.label}</p>
          {item.detail && <p className="text-sm text-gray-600">{item.detail}</p>}
          {item.fleet && fleet && <p className="text-xs text-gray-500">Fleet: {fleet}</p>}
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (psi, hours, gallons, a reason) — optional"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => answer.mutate({ key: item.key, result: 'pass' })} disabled={answer.isLoading}
              className="py-3.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm disabled:opacity-50">Done</button>
            <button onClick={() => answer.mutate({ key: item.key, result: 'hold' })} disabled={answer.isLoading}
              className="py-3.5 rounded-xl bg-red-600 text-white font-semibold text-sm disabled:opacity-50">Hold</button>
            <button onClick={() => answer.mutate({ key: item.key, result: 'skip' })} disabled={answer.isLoading}
              className="py-3.5 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm disabled:opacity-50">Skip</button>
          </div>
          <p className="text-[11px] text-gray-500">A note on its own counts as done. Hold = stays in the yard.</p>
          {p.answered > 0 && (
            <button onClick={() => { if (window.confirm('Mark the rest done?')) passRest.mutate(); }} className="text-xs text-gray-500 underline">Rest are done</button>
          )}
        </div>
      ) : null}

      {check.holds?.length > 0 && view !== 'edit' && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs uppercase tracking-widest text-red-700 font-semibold">Hold list — stays in the yard</p>
          <ul className="mt-1 text-sm text-red-900 space-y-0.5">
            {check.holds.map((h: any) => <li key={h.key}>• {h.label}{h.note ? ` — ${h.note}` : ''}</li>)}
          </ul>
        </div>
      )}

      {data?.history?.length > 0 && view === 'item' && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer">Last checks</summary>
          <ul className="mt-1 font-mono space-y-0.5">{data.history.map((h: any) => <li key={h.id}>{h.log}</li>)}</ul>
        </details>
      )}
    </div>
  );
}

function CompleteBlock({ s, onWeekly, onAdjust, onRestart }: { s: any; onWeekly: () => void; onAdjust: () => void; onRestart: () => void }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
      <p className="text-xs uppercase tracking-widest text-gray-500">—— Check complete ——</p>
      <dl className="text-sm grid grid-cols-[7rem_1fr] gap-y-1">
        <dt className="text-gray-500">Mode</dt><dd className="text-gray-900">{s.mode_label}</dd>
        <dt className="text-gray-500">Date</dt><dd className="text-gray-900">{s.day}</dd>
        <dt className="text-gray-500">Passed</dt><dd className="text-gray-900">{s.passed}</dd>
        <dt className="text-gray-500">Held</dt><dd className="text-gray-900">{s.held}{s.holds.length ? ` — ${s.holds.join('; ')}` : '  None'}</dd>
        <dt className="text-gray-500">Skipped</dt><dd className="text-gray-900">{s.skipped}</dd>
        <dt className="text-gray-500">Notes</dt><dd className="text-gray-900">{s.notes.length ? s.notes.join('; ') : 'None'}</dd>
      </dl>
      <div className={`rounded-xl px-4 py-3 text-center font-bold text-lg ${s.release === 'YES' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
        Release truck: {s.release}
      </div>
      <p className="font-mono text-xs text-gray-600 break-words">{s.log}</p>
      <p className="text-sm text-gray-700">Start weekly shop check, adjust the list, or done for now?</p>
      <div className="grid grid-cols-3 gap-2 text-xs font-semibold">
        <button onClick={onWeekly} className="py-2.5 rounded-lg bg-gray-900 text-white">Weekly check</button>
        <button onClick={onAdjust} className="py-2.5 rounded-lg border border-gray-300 text-gray-700">Adjust list</button>
        <button onClick={() => { if (window.confirm('Start this check over? Today\'s answers go.')) onRestart(); }} className="py-2.5 rounded-lg border border-gray-300 text-gray-500">Start over</button>
      </div>
    </div>
  );
}

function ListEditor({ lists, mode, onDone }: { lists: any; mode: Mode; onDone: () => void }) {
  const [morning, setMorning] = useState<any[]>(lists.morning);
  const [weekly, setWeekly] = useState<any[]>(lists.weekly);
  const [fleet, setFleet] = useState<string>(lists.fleet || '');
  const [adding, setAdding] = useState('');
  const list = mode === 'morning' ? morning : weekly;
  const setList = mode === 'morning' ? setMorning : setWeekly;
  const save = useMutation(() => api.put('/manager-check/lists', { morning, weekly, fleet }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); onDone(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save the list'); },
  });
  const reset = useMutation(() => api.post('/manager-check/lists/reset', { mode }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); (mode === 'morning' ? setMorning : setWeekly)(d.lists[mode]); },
  });
  const move = (i: number, d: number) => setList(l => { const n = [...l]; const j = i + d; if (j < 0 || j >= n.length) return l; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const moveTo = (i: number) => {
    const item = list[i];
    setList(l => l.filter((_, k) => k !== i));
    (mode === 'morning' ? setWeekly : setMorning)(l => [...l, item]);
    toast.info(`Moved to ${mode === 'morning' ? 'weekly' : 'morning'}`);
  };
  const field = 'border border-gray-300 rounded-lg px-2 py-1 text-sm';
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Adjust the {mode} list</p>
      <label className="block text-xs text-gray-600">Mowers (used in the oil / tires / start wording)
        <input value={fleet} onChange={e => setFleet(e.target.value)} placeholder="2 zero-turns, 1 walk-behind" className={`${field} w-full mt-1`} />
      </label>
      <ul className="space-y-2">
        {list.map((i, idx) => (
          <li key={i.key || idx} className="rounded-lg border border-gray-100 p-2 space-y-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 w-5">{idx + 1}.</span>
              <input value={i.label} onChange={e => setList(l => l.map((x, k) => k === idx ? { ...x, label: e.target.value } : x))} className={`${field} flex-1`} />
              <button onClick={() => move(idx, -1)} className="p-1 text-gray-500"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(idx, 1)} className="p-1 text-gray-500"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => setList(l => l.filter((_, k) => k !== idx))} className="p-1 text-gray-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center gap-1 pl-6">
              <input value={i.detail || ''} onChange={e => setList(l => l.map((x, k) => k === idx ? { ...x, detail: e.target.value } : x))} placeholder="The one line under it (optional)" className={`${field} flex-1 text-xs`} />
              <button onClick={() => moveTo(idx)} className="text-[11px] text-gray-500 underline whitespace-nowrap">to {mode === 'morning' ? 'weekly' : 'morning'}</button>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input value={adding} onChange={e => setAdding(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && adding.trim()) { setList(l => [...l, { label: adding.trim(), detail: '' }]); setAdding(''); } }}
          placeholder="Add an item" className={`${field} flex-1`} />
        <button onClick={() => { if (adding.trim()) { setList(l => [...l, { label: adding.trim(), detail: '' }]); setAdding(''); } }} className="px-3 rounded-lg border border-gray-300 text-gray-700"><Plus className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-2">
        <button onClick={() => save.mutate()} disabled={save.isLoading} className="btn-primary flex-1 text-sm disabled:opacity-50">{save.isLoading ? 'Saving…' : 'Save list'}</button>
        <button onClick={() => { if (window.confirm(`Put the default ${mode} list back?`)) reset.mutate(); }} className="btn-secondary text-sm">Reset</button>
        <button onClick={onDone} className="btn-secondary text-sm">Back</button>
      </div>
      <p className="text-[11px] text-gray-500">A check already running today keeps its own copy of the items; the new list starts with the next check.</p>
    </div>
  );
}
